import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Plus, Send } from 'lucide-react';
import { PageHeader, TableSkeleton } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/misc';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormError } from '@/features/auth/auth-layout';
import { ApiRequestError } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useCampaigns, useSaveCampaign, useSegments } from './api';
import { CAMPAIGN_STATUS_LABELS, CAMPAIGN_STATUS_VARIANT, CHANNEL_LABELS } from './labels';

export function CampaignsPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [creating, setCreating] = useState(false);
  const { data: campaigns = [], isLoading } = useCampaigns({ status: status || undefined });

  return (
    <div>
      <PageHeader
        title="Campaigns"
        description="Email and WhatsApp broadcasts. Every send is checked against the suppression list, consent and the weekly frequency cap first."
        actions={
          <Can permission="marketing.campaign.create">
            <Button onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" />
              New campaign
            </Button>
          </Can>
        }
      />

      <div className="mb-4 flex flex-wrap gap-2">
        <Select
          className="h-9 w-auto min-w-[10rem]"
          value={status}
          onChange={(event) => setStatus(event.target.value)}
        >
          <option value="">All statuses</option>
          {Object.entries(CAMPAIGN_STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
      </div>

      {isLoading ? (
        <TableSkeleton columns={5} />
      ) : campaigns.length === 0 ? (
        <Card>
          <CardContent className="py-14 text-center">
            <Mail className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              No campaigns yet. Build an audience first, then write to it.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {campaigns.map((campaign) => (
            <Card
              key={campaign.id}
              className="cursor-pointer transition-shadow hover:shadow-brand"
              onClick={() => navigate(`/marketing/campaigns/${campaign.id}`)}
            >
              <CardContent className="p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium">{campaign.name}</h3>
                      <Badge variant={CAMPAIGN_STATUS_VARIANT[campaign.status]}>
                        {CAMPAIGN_STATUS_LABELS[campaign.status]}
                      </Badge>
                      {campaign.abEnabled && (
                        <Badge variant="secondary" className="text-[10px]">
                          A/B
                          {campaign.abWinnerKey && ` · ${campaign.abWinnerKey} won`}
                        </Badge>
                      )}
                    </div>
                    <p className="mt-0.5 truncate text-sm text-muted-foreground">
                      {campaign.subject ?? 'No subject yet'}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {CHANNEL_LABELS[campaign.channel]}
                      {campaign.segment && ` · ${campaign.segment.name}`}
                      {campaign.sentAt
                        ? ` · sent ${formatDate(campaign.sentAt)}`
                        : campaign.scheduledAt
                          ? ` · scheduled ${formatDate(campaign.scheduledAt)}`
                          : ''}
                    </p>
                  </div>

                  {campaign.stats.total > 0 && (
                    <div className="flex gap-5 text-end text-sm">
                      <Stat label="Sent" value={campaign.stats.sent} />
                      <Stat
                        label="Opened"
                        value={campaign.stats.opened}
                        rate={campaign.stats.openRate}
                      />
                      <Stat
                        label="Clicked"
                        value={campaign.stats.clicked}
                        rate={campaign.stats.clickRate}
                      />
                      {campaign.stats.skipped > 0 && (
                        <Stat label="Skipped" value={campaign.stats.skipped} muted />
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {creating && (
        <NewCampaignDialog
          onClose={() => setCreating(false)}
          onCreated={(id) => navigate(`/marketing/campaigns/${id}`)}
        />
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  rate,
  muted,
}: {
  label: string;
  value: number;
  rate?: number | null;
  muted?: boolean;
}) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={cn('tabular font-semibold', muted && 'text-muted-foreground')}>
        {value}
        {rate !== null && rate !== undefined && (
          <span className="ms-1 text-xs font-normal text-muted-foreground">{rate}%</span>
        )}
      </div>
    </div>
  );
}

function NewCampaignDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const save = useSaveCampaign();
  const { data: segments = [] } = useSegments();
  const [form, setForm] = useState({ name: '', channel: 'EMAIL', segmentId: '' });
  const [error, setError] = useState<string | null>(null);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New campaign</DialogTitle>
          <DialogDescription>
            You can change the audience and the content on the next screen.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input
              value={form.name}
              placeholder="September newsletter"
              onChange={(event) => setForm({ ...form, name: event.target.value })}
            />
            <p className="text-xs text-muted-foreground">
              Internal only — recipients never see it.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Channel</Label>
              <Select
                value={form.channel}
                onChange={(event) => setForm({ ...form, channel: event.target.value })}
              >
                <option value="EMAIL">Email</option>
                <option value="WHATSAPP">WhatsApp</option>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Audience</Label>
              <Select
                value={form.segmentId}
                onChange={(event) => setForm({ ...form, segmentId: event.target.value })}
              >
                <option value="">Choose later</option>
                {segments.map((segment) => (
                  <option key={segment.id} value={segment.id}>
                    {segment.name}
                    {segment.memberCount !== null && ` (${segment.memberCount})`}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <FormError message={error} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={save.isPending || form.name.trim().length < 2}
            onClick={async () => {
              setError(null);
              try {
                const campaign = await save.mutateAsync({
                  name: form.name,
                  channel: form.channel,
                  segmentId: form.segmentId || undefined,
                });
                onCreated(campaign.id);
              } catch (caught) {
                setError(
                  caught instanceof ApiRequestError ? caught.message : 'Could not create it.',
                );
              }
            }}
          >
            <Send className="h-4 w-4" />
            Create campaign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
